import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";

function base64ToBlob(base64Data, contentType = "") {
  const sliceSize = 1024;
  const byteCharacters = atob(base64Data);
  const bytesLength = byteCharacters.length;
  const slicesCount = Math.ceil(bytesLength / sliceSize);
  const byteArrays = [];

  for (let sliceIndex = 0; sliceIndex < slicesCount; sliceIndex++) {
    const begin = sliceIndex * sliceSize;
    const end = Math.min(begin + sliceSize, bytesLength);
    const bytes = new Array(end - begin);
    for (let offset = begin, i = 0; offset < end; offset++, i++) {
      bytes[i] = byteCharacters[offset].charCodeAt(0);
    }
    byteArrays.push(new Uint8Array(bytes));
  }
  return new Blob(byteArrays, { type: contentType });
}

export default function FileThumbnailsEstimates({
  filePath,
  onDelete,
  showDelete = true, // 🔹 Prop opcional para ocultar el icono
}) {
  const { mimeType, data, fileName } = filePath || {};
  const [openConfirm, setOpenConfirm] = useState(false);

  const handleOpenConfirm = () => setOpenConfirm(true);
  const handleCloseConfirm = () => setOpenConfirm(false);

  const handleConfirmDelete = () => {
    if (onDelete && fileName) {
      onDelete(fileName);
    } else {
      console.error("No se encontró la función o el nombre del archivo.");
    }
    setOpenConfirm(false);
  };

  if (!data) {
    return <Typography>No file available</Typography>;
  }

  const renderDeleteButton = () =>
    showDelete && onDelete ? (
      <Tooltip title="Eliminar archivo">
        <IconButton color="error" onClick={handleOpenConfirm}>
          <DeleteOutlinedIcon />
        </IconButton>
      </Tooltip>
    ) : null;

  const fileBoxStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    border: "1px solid #f2f2f2",
    padding: "0.5em",
    borderRadius: "4px",
    background: "#f9f9f9",
    marginBottom: "0.5em",
  };

  // === RENDER PDF ===
  if (mimeType === "application/pdf") {
    const pdfBlob = base64ToBlob(data, mimeType);
    const pdfUrl = URL.createObjectURL(pdfBlob);
    return (
      <>
        <Box sx={fileBoxStyle}>
          <a href={pdfUrl} target="_blank" rel="noreferrer">
            <Typography
              sx={{
                color: "#90b2b7",
                fontWeight: 600,
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              {fileName || "Ver PDF"}
            </Typography>
          </a>
          {renderDeleteButton()}
        </Box>

        <Dialog open={openConfirm} onClose={handleCloseConfirm}>
          <DialogTitle>Confirmar eliminación</DialogTitle>
          <DialogContent>
            <DialogContentText>
              ¿Seguro que deseas eliminar el archivo <strong>{fileName}</strong>
              ? Esta acción no se puede deshacer.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseConfirm} color="inherit">
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmDelete}
              color="error"
              variant="contained"
            >
              Eliminar
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  // === RENDER IMAGEN ===
  if (mimeType?.includes("image")) {
    const imgSrc = `data:${mimeType};base64,${data}`;
    return (
      <>
        <Box sx={fileBoxStyle}>
          <img
            src={imgSrc}
            alt={fileName || "Preview"}
            style={{
              maxWidth: "80%",
              maxHeight: "100%",
              objectFit: "contain",
            }}
          />
          {renderDeleteButton()}
        </Box>

        <Dialog open={openConfirm} onClose={handleCloseConfirm}>
          <DialogTitle>Confirmar eliminación</DialogTitle>
          <DialogContent>
            <DialogContentText>
              ¿Seguro que deseas eliminar la imagen <strong>{fileName}</strong>?
              Esta acción no se puede deshacer.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseConfirm} color="inherit">
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmDelete}
              color="error"
              variant="contained"
            >
              Eliminar
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  // === RENDER OTRO TIPO ===
  return (
    <>
      <Box sx={fileBoxStyle}>
        <Typography>Unsupported file type: {mimeType}</Typography>
        {renderDeleteButton()}
      </Box>

      <Dialog open={openConfirm} onClose={handleCloseConfirm}>
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Seguro que deseas eliminar este archivo? Esta acción no se puede
            deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirm} color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
